import resolveReduction from './resolveReduction.js';
import { foregroundMajorityOfBox } from './foregroundMajorityPlane.js';
import { spanOfBox, weightInSpan } from './boxSpan.js';
function hasSeveralComponents(source) {
    return ((source.numberOfComponents ?? 1) > 1);
}
function sliceReaderOf(source, { allowPublic }) {
    const view = source._getSliceView?.bind(source);
    const direct = source._getSliceData?.bind(source);
    const fallback = direct ??
        (allowPublic
            ? source.getSliceData?.bind(source)
            : undefined);
    if (!fallback) {
        return undefined;
    }
    return (sliceIndex) => view?.(sliceIndex) ?? fallback({ sliceIndex, slicePlane: 2 });
}
function directTargetOf(target) {
    const scalars = target.getWritableScalarData?.();
    const [width, height] = target.dimensions ?? [];
    return scalars && width && height && !hasSeveralComponents(target)
        ? { scalars, width, height }
        : undefined;
}
function directIndexOf({ width, height }, i, j, k) {
    return (k * height + j) * width + i;
}
function reduceForegroundMajorityBySlices(source, reduction, target) {
    const readSlice = sliceReaderOf(source, { allowPublic: false });
    if (!readSlice || hasSeveralComponents(source)) {
        return undefined;
    }
    const { factors, sourceOffset, sourceEnd, dimensions } = resolveReduction(source, reduction);
    const [factorI, factorJ, factorK] = factors;
    const [sourceWidth] = source.dimensions;
    const frameLength = sourceWidth * Math.ceil(sourceEnd[1]);
    const [targetI, targetJ, targetK] = reduction.targetOffset ?? [0, 0, 0];
    const labels = [];
    const counts = [];
    const frames = [];
    const frameWeights = [];
    let written = 0;
    const direct = directTargetOf(target);
    for (let k = 0; k < dimensions[2]; k++) {
        const spanK = spanOfBox(sourceOffset[2], factorK, sourceEnd[2], k);
        frames.length = 0;
        frameWeights.length = 0;
        for (let sourceK = spanK.first; sourceK < spanK.end; sourceK++) {
            const frame = readSlice(sourceK);
            if (!frame) {
                continue;
            }
            if (frame.length < frameLength ||
                frame instanceof Float32Array ||
                frame instanceof Float64Array) {
                return undefined;
            }
            frames.push(frame);
            frameWeights.push(weightInSpan(spanK, sourceK));
        }
        if (!frames.length) {
            continue;
        }
        for (let j = 0; j < dimensions[1]; j++) {
            const spanJ = spanOfBox(sourceOffset[1], factorJ, sourceEnd[1], j);
            const base = direct
                ? directIndexOf(direct, targetI, targetJ + j, targetK + k)
                : 0;
            for (let i = 0; i < dimensions[0]; i++) {
                const majority = foregroundMajorityOfBox(frames, frameWeights, sourceWidth, spanOfBox(sourceOffset[0], factorI, sourceEnd[0], i), spanJ, labels, counts);
                if (direct) {
                    direct.scalars[base + i] = majority;
                }
                else {
                    target.setAtIJK(targetI + i, targetJ + j, targetK + k, majority);
                }
                written++;
            }
        }
    }
    return written;
}
function reduceAverageAlongKBySlices(source, reduction, target, { round }) {
    if ((typeof source._getSliceData !==
        'function' &&
        typeof source.getSliceData !== 'function') ||
        hasSeveralComponents(source)) {
        return undefined;
    }
    const resolved = resolveReduction(source, reduction);
    const [factorI, factorJ, factorK] = resolved.factors;
    if (factorI !== 1 || factorJ !== 1 || factorK <= 1) {
        return undefined;
    }
    const { sourceOffset, sourceEnd, dimensions: reducedDims } = resolved;
    const [sourceWidth] = source.dimensions;
    const [reducedWidth, reducedHeight, reducedDepth] = reducedDims;
    const [targetI, targetJ, targetK] = reduction.targetOffset ?? [0, 0, 0];
    if (sourceEnd[0] - sourceOffset[0] !== reducedWidth ||
        sourceEnd[1] - sourceOffset[1] !== reducedHeight) {
        return undefined;
    }
    const readSlice = sliceReaderOf(source, {
        allowPublic: source.getWritableScalarData?.() !== undefined,
    });
    if (!readSlice) {
        return undefined;
    }
    let written = 0;
    const direct = directTargetOf(target);
    const frames = [];
    const frameWeights = [];
    for (let rk = 0; rk < reducedDepth; rk++) {
        const spanK = spanOfBox(sourceOffset[2], factorK, sourceEnd[2], rk);
        frames.length = 0;
        frameWeights.length = 0;
        for (let k = spanK.first; k < spanK.end; k++) {
            let frame;
            try {
                frame = readSlice(k);
            }
            catch {
                frame = undefined;
            }
            if (!frame ||
                frame.length < sourceWidth * (sourceOffset[1] + reducedHeight)) {
                continue;
            }
            frames.push(frame);
            frameWeights.push(weightInSpan(spanK, k));
        }
        if (!frames.length) {
            continue;
        }
        for (let j = 0; j < reducedHeight; j++) {
            const sourceRow = (sourceOffset[1] + j) * sourceWidth + sourceOffset[0];
            const base = direct
                ? directIndexOf(direct, targetI, targetJ + j, targetK + rk)
                : 0;
            for (let i = 0; i < reducedWidth; i++) {
                const index = sourceRow + i;
                let sum = 0;
                let count = 0;
                for (let m = 0; m < frames.length; m++) {
                    const value = frames[m][index];
                    if (Number.isFinite(value)) {
                        sum += value * frameWeights[m];
                        count += frameWeights[m];
                    }
                }
                if (!count) {
                    continue;
                }
                const value = round ? Math.round(sum / count) : sum / count;
                if (direct) {
                    direct.scalars[base + i] = value;
                }
                else {
                    target.setAtIJK(targetI + i, targetJ + j, targetK + rk, value);
                }
                written++;
            }
        }
    }
    return written;
}
export { reduceForegroundMajorityBySlices, reduceAverageAlongKBySlices };
